import React from 'react';
import { Many, sortBy } from 'lodash';
import { substringSearchFilter } from '@neptune/shared/search-business-logic';
import { FilterResultItem, QueryFilterSearch } from '@neptune/shared/search-domain';
import {
  AttributeDefinition,
  attributeDefinitionComparator,
  AttributeType,
  convertAttributeToSearchString,
} from 'domain/experiment/attribute';
import { UnwindSubpropertyStrategy } from './unwind-property-strategy';
import { useAttributeDefinitionQuery } from './use-attribute-definition-query';
import { buildSearchLeaderboardQuery } from './buildSearchLeaderboardQuery';

type LodashCompatibleSortByIteratee<T> = Many<
  (value: FilterResultItem<T>, index: number, collection: ArrayLike<FilterResultItem<T>>) => unknown
>;

const always: (item: unknown) => boolean = () => true;

type UseAttributeDefinitionDataSourceProps<T extends AttributeDefinition> = {
  defaultFilterAttributeTypes?: AttributeType[];
  enhancer: (input: AttributeDefinition) => T;
  entityId: string;
  entityType: string;
  excludeAttributes?: AttributeDefinition[];
  filter?: (input: T) => boolean;
  limit?: number;
  prefillAttributes?: AttributeDefinition[];
  projectIdentifier: string;
  sortByIteratee?: LodashCompatibleSortByIteratee<AttributeDefinition>;
  subpropertyStrategy: UnwindSubpropertyStrategy;
  trash: boolean;
  types: string[];
};

const LIMIT_ATTRIBUTES = 50;

export const useAttributeDefinitionDataSource = <
  T extends AttributeDefinition = AttributeDefinition,
>({
  defaultFilterAttributeTypes,
  enhancer,
  entityId,
  entityType,
  excludeAttributes,
  filter = always,
  limit = LIMIT_ATTRIBUTES,
  prefillAttributes,
  projectIdentifier,
  sortByIteratee,
  subpropertyStrategy,
  trash,
  types,
}: UseAttributeDefinitionDataSourceProps<T>): QueryFilterSearch<
  T,
  {
    predicate: (item: T) => boolean;
    filterAttributeTypes?: AttributeType[];
  }
> => {
  // Current implementation of the limit in the backend applies to one attribute type, it does not limit response entities.
  // We want to make sure (in case when the backend implementation will change again) that we always can get more than
  // limit to show proper search result messages, therefore we want to get one more element than limit.
  const rawQuery = useAttributeDefinitionQuery({
    limit: limit + 1,
    projectIdentifier,
    subpropertyStrategy,
    types,
    query: buildSearchLeaderboardQuery(entityType, entityId, trash),
  });

  const prepareOutput = React.useCallback(
    (input: AttributeDefinition[], search: string, predicate: (item: T) => boolean) => {
      const attributes = input.filter((entry) =>
        prefillAttributes ? !includesAttribute(prefillAttributes, entry) : true,
      );
      const collection = (prefillAttributes ? [...prefillAttributes, ...attributes] : attributes)
        .map(enhancer)
        .filter(filter)
        .filter((entry) =>
          defaultFilterAttributeTypes ? defaultFilterAttributeTypes.includes(entry.type) : true,
        )
        .filter((entry) =>
          excludeAttributes ? !includesAttribute(excludeAttributes, entry) : true,
        )
        .filter(predicate);
      const allResults = substringSearchFilter(search, collection, convertAttributeToSearchString);
      const allResultsSorted = sortByIteratee ? sortBy(allResults, sortByIteratee) : allResults;
      return allResultsSorted;
    },
    [
      defaultFilterAttributeTypes,
      enhancer,
      excludeAttributes,
      filter,
      prefillAttributes,
      sortByIteratee,
    ],
  );

  return React.useCallback(
    async (search: string, params = { predicate: always }) => {
      const filterAttributeTypes = params?.filterAttributeTypes ?? defaultFilterAttributeTypes;
      const collection = await rawQuery(search.trim(), filterAttributeTypes);
      const entries = prepareOutput(collection, search.trim(), params.predicate);
      const limitedEntries = entries.slice(0, limit);
      return {
        entries: limitedEntries,
        collectionCount: entries.length,
      };
    },
    [defaultFilterAttributeTypes, rawQuery, prepareOutput, limit],
  );
};

function includesAttribute(collection: AttributeDefinition[], entry: AttributeDefinition) {
  return collection.some((used) => attributeDefinitionComparator(entry, used));
}
